import { useState } from 'react';
import {
  EDAD_MAXIMA,
  EDAD_MINIMA,
  MAXIMO_DEL_NOMBRE,
  type DatosDelPerfil,
  type TextosDelPerfil,
  type Tratamiento,
} from './api';

/**
 * Quién es la persona, preguntado antes de las setenta y cinco preguntas.
 *
 * POR QUÉ ESTÁ AQUÍ Y NO EN WHATSAPP. El acompañante no le habla a nadie hasta
 * que termina el cuestionario, así que preguntarlo en esta pantalla llega
 * siempre a tiempo; hacerlo por chat obligaría a interpretar texto libre —«me
 * llamo Ana pero dime Ani»— para sacar un dato que aquí es un campo.
 *
 * NI UNA PALABRA CLÍNICA EN ESTE ARCHIVO, igual que en `App.tsx`. Las tres
 * preguntas, la introducción y los rótulos de las cuatro respuestas los escribe
 * la psicóloga y llegan en `textos`. Lo que sí se escribe aquí son los avisos de
 * la interfaz —«escribe cómo quieres que te llamemos»—, que no dicen nada sobre
 * nadie y aparecen solo cuando falta algo por rellenar.
 */

/** Las cuatro respuestas, en el orden en que se ofrecen. */
const TRATAMIENTOS: readonly { clave: Tratamiento; texto: (t: TextosDelPerfil) => string }[] = [
  { clave: 'femenino', texto: (t) => t.opciones.femenino },
  { clave: 'masculino', texto: (t) => t.opciones.masculino },
  { clave: 'neutro', texto: (t) => t.opciones.neutro },
  { clave: 'sin_decir', texto: (t) => t.opciones.sinDecir },
];

/** Lo que falta por rellenar, si falta algo. `null` cuando se puede enviar. */
function queFalta(nombre: string, tratamiento: Tratamiento | null, edad: string): string | null {
  if (nombre.trim() === '') return 'Escribe cómo quieres que te llamemos.';
  if (tratamiento === null) return 'Elige una de las cuatro respuestas.';

  const numero = Number(edad.trim());
  if (edad.trim() === '' || !Number.isInteger(numero)) return 'Escribe tu edad en números.';
  if (numero < EDAD_MINIMA || numero > EDAD_MAXIMA) {
    return `La edad tiene que estar entre ${String(EDAD_MINIMA)} y ${String(EDAD_MAXIMA)}.`;
  }

  return null;
}

export function Perfil({
  textos,
  enviando,
  fallo,
  alGuardar,
}: {
  textos: TextosDelPerfil;
  enviando: boolean;
  /** Lo que pasó al intentar guardar, si pasó algo. Lo pone quien llama. */
  fallo: string | null;
  alGuardar: (datos: DatosDelPerfil) => void;
}) {
  const [nombre, setNombre] = useState('');
  const [tratamiento, setTratamiento] = useState<Tratamiento | null>(null);
  const [edad, setEdad] = useState('');
  /**
   * Los avisos no aparecen hasta el primer intento de continuar.
   *
   * Marcar en rojo lo que todavía no ha tenido oportunidad de rellenarse
   * convierte una pantalla de bienvenida en una lista de errores, y es la
   * primera pantalla del producto que ve alguien.
   */
  const [intentado, setIntentado] = useState(false);

  const falta = queFalta(nombre, tratamiento, edad);

  return (
    <main className="hoja">
      <img className="marca" src="/marca/logo-sobre-claro.svg" alt="Ser Prana" />

      <p className="introduccion">{textos.introduccion}</p>

      <section className="grupo">
        {/*
         * `fieldset` + `legend` como en `Pregunta.tsx`, y no un `label` suelto:
         * es lo que hace que estas tres tarjetas se vean y se anuncien igual que
         * las del cuestionario que viene detrás. El `aria-labelledby` de cada
         * campo está porque el `legend` nombra al grupo, no al control de dentro.
         */}
        <div className="preguntas">
          <fieldset className="pregunta">
            <legend className="pregunta__enunciado" id="perfil-nombre-enunciado">
              {textos.preguntaNombre}
            </legend>
            <input
              type="text"
              className="campo"
              value={nombre}
              maxLength={MAXIMO_DEL_NOMBRE}
              autoComplete="given-name"
              aria-labelledby="perfil-nombre-enunciado"
              disabled={enviando}
              onChange={(evento) => {
                setNombre(evento.target.value);
              }}
            />
          </fieldset>

          <fieldset className="pregunta">
            <legend className="pregunta__enunciado" id="perfil-trato-enunciado">
              {textos.preguntaTratamiento}
            </legend>
            <div className="opciones" role="radiogroup" aria-labelledby="perfil-trato-enunciado">
              {TRATAMIENTOS.map((opcion) => (
                <button
                  key={opcion.clave}
                  type="button"
                  role="radio"
                  aria-checked={tratamiento === opcion.clave}
                  disabled={enviando}
                  className={`opcion${tratamiento === opcion.clave ? ' opcion--elegida' : ''}`}
                  onClick={() => {
                    setTratamiento(opcion.clave);
                  }}
                >
                  {/* Decorativa: quien la anuncia es el `aria-checked` del botón. */}
                  <span className="opcion__marca opcion__marca--una" aria-hidden="true" />
                  {opcion.texto(textos)}
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset className="pregunta">
            <legend className="pregunta__enunciado" id="perfil-edad-enunciado">
              {textos.preguntaEdad}
            </legend>
            {/*
             * `inputMode="numeric"` y no `type="number"`: en el móvil los dos
             * abren el teclado de números, pero el numérico añade flechas de
             * subir y bajar que se pulsan sin querer al desplazarse, y acepta
             * comas y signos que después hay que limpiar.
             */}
            <input
              type="text"
              inputMode="numeric"
              className="campo"
              value={edad}
              maxLength={3}
              aria-labelledby="perfil-edad-enunciado"
              disabled={enviando}
              onChange={(evento) => {
                setEdad(evento.target.value.replace(/\D/g, ''));
              }}
            />
          </fieldset>
        </div>
      </section>

      {intentado && falta !== null && (
        <p className="aviso aviso--problema" role="alert">
          {falta}
        </p>
      )}

      {fallo !== null && (
        <p className="aviso aviso--problema" role="alert">
          {fallo}
        </p>
      )}

      <div className="botonera">
        {/*
         * El botón no se deshabilita cuando falta algo, y es a propósito: un
         * botón apagado no dice qué le falta. Se pulsa, y el aviso aparece.
         */}
        <button
          type="button"
          className="boton"
          disabled={enviando}
          onClick={() => {
            setIntentado(true);
            if (falta !== null || tratamiento === null) return;
            alGuardar({ nombre: nombre.trim(), tratamiento, edad: Number(edad.trim()) });
          }}
        >
          {enviando ? 'Guardando…' : 'Continuar'}
        </button>
      </div>
    </main>
  );
}
